import React from 'react';
import type ApolloClient from 'apollo-client';
import type { Dispatch, ForgeDoc, ForgeExtensionPoints, RenderFn } from '@atlassian/forge-ui-types';
import { fg } from '@atlaskit/platform-feature-flags';
import SectionMessage from '@atlaskit/section-message';
import Link from '@atlaskit/link';
import { uiKit1Components } from './defaultComponents';
import { Root, Fragment } from '../components/UIKit/internalComponents';
import { type ComponentMap, type ModalExtension } from './util';
import { RendererNext } from './RendererNextComponent';
import { importMapMemo } from '../components/UIKit/importMap';
import { importMapChartsMemo } from '../components/UIKit-charts/importMapCharts';
import { ThreeLOPromptRenderFn } from '../components/threeLOPrompt/threeLOPrompt';
import { ComponentError } from '../error-boundary/ComponentErrorBoundary';
import { Strong } from '../components/UIKit/markup';
import { type Extension } from '../web-client';

export const validComponentsHardcoded: Set<string> = new Set([
	'Badge',
	'Box',
	'Button',
	'ButtonGroup',
	'Calendar',
	'Checkbox',
	'CheckboxGroup',
	'Code',
	'CodeBlock',
	'Comment',
	'DatePicker',
	'DynamicTable',
	'EmptyState',
	'ErrorMessage',
	'Flex',
	'FileCard',
	'FilePicker',
	'Form',
	'FormFooter',
	'FormHeader',
	'FormSection',
	'Grid',
	'Heading',
	'HelperMessage',
	'Icon',
	'Inline',
	'InlineEdit',
	'Label',
	'LinkButton',
	'List',
	'ListItem',
	'LoadingButton',
	'Lozenge',
	'Modal',
	'ModalBody',
	'ModalFooter',
	'ModalHeader',
	'ModalTitle',
	'ModalTransition',
	'Popup',
	'Pressable',
	'ProgressBar',
	'ProgressTracker',
	'Radio',
	'RadioGroup',
	'Range',
	'RequiredAsterisk',
	'SectionMessage',
	'SectionMessageAction',
	'Select',
	'Spinner',
	'Stack',
	'Tab',
	'TabList',
	'TabPanel',
	'Tabs',
	'Tag',
	'TagGroup',
	'TextArea',
	'Textfield',
	'TimePicker',
	'Toggle',
	'Tooltip',
	'ValidMessage',
	'AdfRenderer',
	'Image',
	'Link',
	'Text',
	'Strong',
	'BarChart',
	'StackBarChart',
	'HorizontalStackBarChart',
	'HorizontalBarChart',
	'LineChart',
	'PieChart',
	'DonutChart',
	'String',
	'Em',
	'Strike',
	'Root',
	'Fragment',
	'ThreeLOPrompt',
	'User',
	'UserGroup',
	'UserPicker',
	'Frame',
	'ContentWrapper',
	'ValidationMessage',
	'ButtonSet',
	'DateLozenge',
	'ErrorPanel',
	'FormCondition',
	'InlineDialog',
	'ModalDialog',
	'StatusLozenge',
	'Table',
	'TextField',
	'View',
]);

const rendererNextComponents: ComponentMap = {
	ThreeLOPrompt: ThreeLOPromptRenderFn,
};

// This was extracted from RendererNext Component due to UnknownComponent Error being
// incompatible with running in a server-side context.
const customRenderErrorFn = (forgeDocType: string) => {
	// Buckets errors into two kinds, ComponentError - component errors we want to report on e.g. <Button/> vs ones we don't e.g. <Buton/> which will only show up in the users console
	if (validComponentsHardcoded.has(forgeDocType)) {
		throw new ComponentError(`Error rendering app - component ${forgeDocType}.`, forgeDocType);
	} else {
		throw new Error(`Error rendering app - encountered unknown component ${forgeDocType}.`);
	}
};

type Props = {
	/** Apollo client to use for all product GraphQL queries */
	client?: ApolloClient<any>; // to be removed with forge_disable_fcf_modal_experience_on_issue_view fg cleanup
	/* Map of component types to render functions */
	components?: (defaults: ComponentMap) => ComponentMap;
	// defaultComponents: ComponentMap;
	/* Function used by components to dispatch effects */
	dispatch: Dispatch;
	/* Error message to show the user. Set when an unexpected client-side error happens. */
	error?: string;
	/* Contains all the details for the extension*/
	extension?: Extension; // to be removed with forge_disable_fcf_modal_experience_on_issue_view fg cleanup
	/* Contains the module type of the extension */
	extensionType?: ForgeExtensionPoints;
	/* ForgeDoc to be rendered */
	forgeDoc?: ForgeDoc;
	/* Indicates whether a CSUIKit app is being rendered*/
	isNative?: boolean;
	/* Whether a dispatched effect is pending. Not included in function due to server side render*/
	loading?: boolean;
	/* Replace the default spinner with a custom loading component. */
	loadingComponent?: React.ReactNode;
	/* Object that stores all necessary properties for a Modal Extension */
	modalExtension?: ModalExtension;
	/* Callback for a side effect to perform on success from the consuming app */
	onSuccess?: () => void;
};

export const RendererNextLatest = (props: Props): React.JSX.Element => {
	const uiKitComponents = Object.keys(importMapMemo).reduce((acc, key) => {
		// Restricts FilePicker and FileCard components to only be used in EAP
		if (
			!fg('forge-ui-display-object-store-components') &&
			(key === 'FilePicker' || key === 'FileCard')
		) {
			return {
				...acc,
				[key]: ((args) => (
					// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
					<SectionMessage appearance="error" key={args.forgeDoc.key}>
						FilePicker and FileCard components are in EAP. <br />
						To start testing,{' '}
						<Link
							href="https://ecosystem.atlassian.net/servicedesk/customer/portal/1040/group/3496/create/18937"
							target="_blank"
							// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
						>
							sign up here
						</Link>
						.
					</SectionMessage>
				)) as RenderFn,
			};
		}

		const Component = importMapMemo[key];
		return {
			...acc,
			[key]: ((args) => <Component {...args} key={args.forgeDoc.key} />) as RenderFn,
		};
	}, {});

	const uiKitChartsComponents = Object.keys(importMapChartsMemo).reduce((acc, key) => {
		const Component = importMapChartsMemo[key];
		return {
			...acc,
			[key]: ((args) => <Component {...args} key={args.forgeDoc.key} />) as RenderFn,
		};
	}, {});

	const components = {
		...uiKitComponents,
		...uiKitChartsComponents,
		String: uiKit1Components.String,
		Em: uiKit1Components.Em,
		Strong: Strong,
		Strike: uiKit1Components.Strike,
		Root,
		Fragment,
	};

	const combinedComponents = {
		...components,
		...rendererNextComponents,
	};

	return (
		<RendererNext
			{...props}
			components={props.components ? props.components(combinedComponents) : combinedComponents}
			customRenderErrorFn={customRenderErrorFn}
		/>
	);
};

export const RendererNextLegacy = (props: Props): React.JSX.Element => {
	const combinedComponents = {
		...uiKit1Components,
		...rendererNextComponents,
	};

	return (
		<RendererNext
			{...props}
			components={props.components ? props.components(combinedComponents) : combinedComponents}
			customRenderErrorFn={customRenderErrorFn}
		/>
	);
};
